import {ActionIcon, Tooltip} from '@mantine/core';
import {MdDelete} from 'react-icons/md';
import {cn} from '../../../../../../lib/classname';
import {useMediaQuery} from '../../../../../../shared/services/useMediaQuery';
import {isNewlyCreatedStep} from '../../../../usecase/stepUtils';
import {ClickToEditDateInputContainer} from '../../ClickToEditDateInputContainer';
import {AiTodoGenerateButtonWithTooltip} from './AiTodoGenerateButtonWithTooltip';
import {ClickToEditInput} from './ClickToEditInput';
import {DeadlineText} from './DeadlineText';
import {EventDateText} from './EventDateText';
import {NotesText} from './NotesText';
import {PlaceText} from './PlaceText';
import {SelectStepResult} from './SelectStepResult';

/**
 * 開始日時と終了日時が両方設定されているかをチェック
 * @param start - 開始日時（未設定の場合はundefined）
 * @param end - 終了日時（未設定の場合はundefined）
 * @returns 両方設定されている場合はtrue、それ以外はfalse
 * @description レイアウト調整に使用。両方設定されている場合は日程を横並び表示
 */
const hasCompleteDateRange = (
  start: string | undefined,
  end: string | undefined,
): boolean => {
  return Boolean(start && end);
};

/**
 * フィールドコンテンツラッパーコンポーネント
 */
const FieldContent: React.FC<{children: React.ReactNode}> = ({children}) => (
  <div className="flex-1">{children}</div>
);

/**
 * フィールドラベルコンポーネント
 */
const FieldLabel: React.FC<{children: React.ReactNode}> = ({children}) => (
  <span className="text-sm pc:text-xs text-black-3 min-w-[50px] pc:min-w-[60px] flex justify-start items-center shrink-0">
    {children}
  </span>
);

/**
 * フィールド行レイアウトコンポーネント
 */
const FieldRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({label, children}) => (
  <div className={'flex gap-4 pc:gap-8'}>
    <FieldLabel>{label}</FieldLabel>
    {children}
  </div>
);

type Props = {
  stepId: number;
  stepName: string;
  deadline?: string;
  result: string | null;
  notes: string;
  startDate?: string;
  endDate?: string;
  place?: string;
  created: string;
  onResultChange?: (stepId: number, result: string) => void;
  onStepFieldUpdate?: (
    stepId: number,
    field:
      | 'stepName'
      | 'deadline'
      | 'startDate'
      | 'endDate'
      | 'place'
      | 'notes',
    value: string | undefined,
  ) => void;
  onGenerateTodos?: (stepId: number) => void;
  isGenerating?: boolean;
  shouldShowGeneratePopover: boolean;
  onDeleteStep?: (stepId: number) => void;
};

export const StepSection: React.FC<Props> = ({
  stepId,
  stepName,
  deadline,
  result,
  notes,
  startDate,
  endDate,
  place,
  created,
  onResultChange,
  onStepFieldUpdate,
  onGenerateTodos,
  isGenerating = false,
  shouldShowGeneratePopover,
  onDeleteStep,
}) => {
  const {isPC} = useMediaQuery();
  const isNew = isNewlyCreatedStep(created);

  const handleResultChange = (newResult: string) => {
    if (onResultChange) {
      onResultChange(stepId, newResult);
    }
  };

  const handleGenerateTodos = () => {
    if (onGenerateTodos) {
      onGenerateTodos(stepId);
    }
  };

  const handleDeleteClick = () => {
    if (onDeleteStep) {
      onDeleteStep(stepId);
    }
  };

  const wrappedOnGenerateTodos = onGenerateTodos
    ? handleGenerateTodos
    : undefined;

  return (
    <div className="pc:relative flex flex-col pc:block w-full pr-12">
      {/* 結果選択とアクション */}
      <div className="pc:absolute pc:right-0 pc:top-0 flex items-center justify-end gap-4 shrink-0 pb-8">
        <AiTodoGenerateButtonWithTooltip
          isNewlyCreated={isNew}
          shouldShowPopover={shouldShowGeneratePopover}
          isGenerating={isGenerating}
          onGenerateTodos={wrappedOnGenerateTodos}
        />
        <Tooltip label="選考予定を削除" withArrow withinPortal>
          <ActionIcon
            variant="transparent"
            onClick={handleDeleteClick}
            disabled={!onDeleteStep}
            title="選考予定を削除"
            className="hover:opacity-60"
          >
            <MdDelete size={20} />
          </ActionIcon>
        </Tooltip>
      </div>

      {/* フォームフィールド */}
      <div className="flex flex-col gap-2 pc:gap-4 w-full">
        {onStepFieldUpdate ? (
          <>
            <div className="w-full pc:w-[calc(100%-160px)]">
              {/* 削除ボタンなどの領域調整 */}
              <FieldRow label="予定">
                <FieldContent>
                  <ClickToEditInput
                    value={stepName}
                    onSave={(value) =>
                      onStepFieldUpdate(stepId, 'stepName', value)
                    }
                    placeholder="ステップ名"
                    displayClassName="font-medium text-sm pc:text-base"
                  />
                </FieldContent>
              </FieldRow>
            </div>

            <FieldRow label="締切">
              <FieldContent>
                <ClickToEditDateInputContainer
                  value={deadline !== '未設定' ? deadline : undefined}
                  onSave={(value) =>
                    onStepFieldUpdate(stepId, 'deadline', value)
                  }
                  placeholder="未設定"
                  withTime
                  variant="inline"
                />
              </FieldContent>
            </FieldRow>

            {/* 日程: 開始日時と終了日時を横並びで表示（レスポンシブ対応） */}
            <FieldRow label="日程">
              <div
                className={cn(
                  'flex flex-1 items-center pc:gap-4',
                  hasCompleteDateRange(startDate, endDate)
                    ? 'justify-between pc:justify-start'
                    : 'justify-start gap-4',
                )}
              >
                <ClickToEditDateInputContainer
                  value={startDate}
                  onSave={(value) =>
                    onStepFieldUpdate(stepId, 'startDate', value)
                  }
                  placeholder="未設定"
                  withTime
                  variant="inline"
                />
                <span className="text-sm text-black-3">〜</span>
                <ClickToEditDateInputContainer
                  value={endDate}
                  onSave={(value) =>
                    onStepFieldUpdate(stepId, 'endDate', value)
                  }
                  placeholder="未設定"
                  withTime
                  variant="inline"
                />
              </div>
            </FieldRow>

            <FieldRow label="場所">
              <FieldContent>
                <ClickToEditInput
                  value={place ?? ''}
                  onSave={(value) => onStepFieldUpdate(stepId, 'place', value)}
                  placeholder="未設定"
                />
              </FieldContent>
            </FieldRow>

            <FieldRow label="メモ">
              <FieldContent>
                <ClickToEditInput
                  value={notes}
                  onSave={(value) => onStepFieldUpdate(stepId, 'notes', value)}
                  placeholder="メモを入力..."
                  multiline
                  renderAsLinks
                />
              </FieldContent>
            </FieldRow>

            <FieldRow label={isPC ? 'ステータス' : '結果'}>
              <SelectStepResult
                value={result ?? ''}
                onChange={handleResultChange}
              />
            </FieldRow>
          </>
        ) : (
          <>
            <div className="text-sm pc:text-base">{stepName}</div>
            <DeadlineText deadline={deadline} hasResult={!!result} />
            <EventDateText
              startDate={startDate}
              endDate={endDate}
              hasResult={!!result}
            />
            <PlaceText place={place} />
            {notes && <NotesText>{notes}</NotesText>}
            <div className="text-sm pc:text-base">{result || '未選択'}</div>
          </>
        )}
      </div>
    </div>
  );
};
